// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetBudgetApiParameter } from "@insite/client-framework/Services/BudgetService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBudgetsDataView, getYearEnd } from "@insite/client-framework/Store/Data/Budgets/BudgetsSelectors";
import loadReviews from "@insite/client-framework/Store/Data/Budgets/Handlers/LoadReviews";
import translate from "@insite/client-framework/Translate";
import { BudgetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import {
    Checkbox,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Tooltip,
    Typography,
} from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isTruthy } from "@microsoft/applicationinsights-core-js";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { tableProps } from "@store/Themes/common/props/table";
import useBudgetRules from "@store/Framework/Common/Hooks/useBudgetRules";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as cssLinter from "css";
import React, { FC, SyntheticEvent, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
    };
}

const mapDispatchToProps = {
    loadReviews,
};

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);

    const budgetApiParameter: GetBudgetApiParameter = {
        userProfileId: session.userProfileId || "",
        shipToId: "",
        fiscalYear: state.pages.budgetManagement.getMaintenanceInfoParameter.fiscalYear,
        fullGrid: true,
    };

    return {
        session,
        budgetApiParameter,
        budgetDataView: getBudgetsDataView(state, budgetApiParameter),
    };
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface AccountAllowancesStyles {
    wrapper?: InjectableCss;
    heading?: InjectableCss;
    title?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    dataHeader?: DataTableHeaderProps;
    dataCells?: DataTableCellBaseProps;
    dataCellsDisabled?: DataTableCellBaseProps;
    dataCellsTotals?: DataTableCellBaseProps;
    checkboxStyles?: CheckboxProps;
}

export const accountAllowancesStyles: AccountAllowancesStyles = {
    title: {
        variant: "h4",
        forwardAs: "h2",
        css: css`
            display: inline-block;
            margin: 0;
            border-bottom: 0;
            ${breakpointValues([pxToRem(5), pxToRem(10), null, null, null], "padding-bottom")};
        `,
    },
    loadingOverlay: { minHeight: 80 },
    tableWrapper: tableProps.wrapper,
    dataTable: tableProps.dataTable,
    dataCellsDisabled: {
        css: css`
            color: ${getColor("text.disabled")};
        `,
    },
    dataCellsTotals: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    checkboxStyles: {
        variant: "toggle",
        labelPosition: "left",
        sizeVariant: "small",
        typographyProps: {
            mergeCss: true,
            css: css`
                width: max-content;
                padding-right: ${pxToRem(15)};
            `,
        },
        mergeCss: true,
        css: css`
            justify-content: flex-start;
            ${breakpointValues([pxToRem(5), 0, null, null, null], "padding-bottom")};
            ${breakpoint("xs", "up")} {
                float: right;
                width: min-content;
                padding-top: ${pxToRem(3)};
            }
        `,
    },
};

const styles = accountAllowancesStyles;

function getEndDate(index: number, maintenanceInfo?: BudgetModel) {
    if (!maintenanceInfo) {
        return;
    }
    const review = maintenanceInfo.budgetLineCollection![index + 1];
    if (review) {
        const date = new Date(review.startDate);
        date.setDate(date.getDate() - 1);
        return date;
    }

    return getYearEnd(maintenanceInfo.fiscalYear, maintenanceInfo.fiscalYearEndDate!);
}

const AccountAllowances: FC<Props> = ({
    session,
    budgetApiParameter,
    budgetDataView: baseBudgetDataView,
    fields: { customCSS },
}) => {
    const dispatch = useAppDispatch();
    const [showInactive, setShowInactive] = useState(false);
    const budgetDataView = usePersistentDataView(baseBudgetDataView);
    const { hasRoleWithAllowances, budgetManagementEnabled, carryOverUnusedMonthlyBalances, hasUserEnforcementLevel } =
        useBudgetRules();

    useEffect(() => {
        if (!baseBudgetDataView.isLoading) {
            dispatch(loadReviews(budgetApiParameter));
        }
    }, []);

    const tableRows = useMemo(() => {
        const currentDate = new Date();
        return (
            budgetDataView?.value?.budgetLineCollection?.map((budgetLine, index) => {
                const startDate = new Date(budgetLine.startDate);
                const endDate = getEndDate(index, budgetDataView.value);
                const isPrevious = isTruthy(endDate && currentDate > endDate);
                const isCurrent = isTruthy(startDate && endDate && currentDate >= startDate && currentDate <= endDate);

                return {
                    period: budgetLine.period,
                    isPrevious,
                    isCurrent,
                    startDateDisplay: startDate
                        ? getLocalizedDateTime({
                              dateTime: startDate,
                              language: session.language,
                          })
                        : "",
                    endDate,
                    endDateDisplay: endDate
                        ? getLocalizedDateTime({
                              dateTime: endDate,
                              language: session.language,
                          })
                        : "",
                    currentYearAllocated: budgetLine.currentFiscalYearBudget,
                    currentYearAllocatedDisplay: budgetLine.currentFiscalYearBudgetDisplay,
                    currentYearSpent: budgetLine.currentFiscalYearActual,
                    currentYearSpentDisplay: budgetLine.currentFiscalYearActualDisplay,
                    currentYearBalance: budgetLine.currentFiscalYearVariance,
                    currentYearBalanceDisplay: budgetLine.currentFiscalYearVarianceDisplay,
                };
            }) || []
        );
    }, [budgetDataView]);

    const currentYearTotalsToDate = useMemo(() => {
        return (
            tableRows
                .filter(row => row.isPrevious || row.isCurrent)
                .reduce(
                    (totals, { currentYearAllocated, currentYearSpent, currentYearBalance }) => {
                        totals.allocated += currentYearAllocated || 0;
                        totals.spent += currentYearSpent || 0;
                        totals.balance += currentYearBalance || 0;
                        return totals;
                    },
                    { allocated: 0, spent: 0, balance: 0 },
                ) || 0
        );
    }, [tableRows]);

    if (!budgetManagementEnabled || !hasRoleWithAllowances || !hasUserEnforcementLevel || !budgetDataView.value) {
        return null;
    }

    const onChangeToggle = (event: SyntheticEvent<Element, Event>, value: boolean) => {
        event.preventDefault();
        setShowInactive(!showInactive);
    };

    const customCssWrapper = css`
        ${styles.wrapper?.css}
        ${customCSS}
    `;

    return (
        <StyledWrapper {...styles.wrapper} css={customCssWrapper} data-test-selector="myAccount_allowances">
            <StyledWrapper {...styles.heading}>
                <Typography {...styles.title}>{translate("My Allowances")}</Typography>
                {carryOverUnusedMonthlyBalances && (
                    <Tooltip text={siteMessage("Carry_Over_Monthly_Balances_Tooltip") as string} />
                )}
                {tableRows.length > 1 && (
                    <Checkbox
                        checked={showInactive}
                        onChange={onChangeToggle}
                        data-test-selector="show-inactive-periods-toggle"
                        {...styles.checkboxStyles}
                    >
                        {translate("Show Inactive Periods")}
                    </Checkbox>
                )}
            </StyledWrapper>
            <DataViewLoadingOverlay {...styles.loadingOverlay} dataView={budgetDataView}>
                {!budgetDataView.isLoading && tableRows.length > 0 && (
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable {...styles.dataTable}>
                            <DataTableHead>
                                <DataTableHeader {...styles.dataHeader}>{translate("Period")}</DataTableHeader>
                                <DataTableHeader {...styles.dataHeader}>{translate("Start Date")}</DataTableHeader>
                                <DataTableHeader {...styles.dataHeader}>{translate("End Date")}</DataTableHeader>
                                <DataTableHeader {...styles.dataHeader}>{translate("Allocated")}</DataTableHeader>
                                <DataTableHeader {...styles.dataHeader}>{translate("Spent")}</DataTableHeader>
                                <DataTableHeader {...styles.dataHeader}>{translate("Balance")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {tableRows.map(
                                    ({
                                        period,
                                        isPrevious,
                                        isCurrent,
                                        startDateDisplay,
                                        endDateDisplay,
                                        currentYearAllocatedDisplay,
                                        currentYearSpentDisplay,
                                        currentYearBalanceDisplay,
                                    }) => {
                                        const isActive = (isPrevious && carryOverUnusedMonthlyBalances) || isCurrent;

                                        if (!showInactive && !isActive) {
                                            return null;
                                        }

                                        const cellStyles = isActive ? styles.dataCells : styles.dataCellsDisabled;

                                        return (
                                            <DataTableRow key={period} data-test-selector="myAccount_allowancesRow">
                                                <DataTableCell {...cellStyles}>{period}</DataTableCell>
                                                <DataTableCell {...cellStyles}>{startDateDisplay}</DataTableCell>
                                                <DataTableCell {...cellStyles}>{endDateDisplay}</DataTableCell>
                                                <DataTableCell {...cellStyles}>
                                                    {currentYearAllocatedDisplay}
                                                </DataTableCell>
                                                <DataTableCell {...cellStyles}>{currentYearSpentDisplay}</DataTableCell>
                                                <DataTableCell {...cellStyles}>
                                                    {currentYearBalanceDisplay}
                                                </DataTableCell>
                                            </DataTableRow>
                                        );
                                    },
                                )}
                                {tableRows.length > 1 && carryOverUnusedMonthlyBalances && (
                                    <DataTableRow data-test-selector="myAccount_allowancesRow">
                                        <DataTableCell {...styles.dataCellsTotals}>
                                            {translate("Totals to date:")}
                                        </DataTableCell>
                                        <DataTableCell {...styles.dataCellsTotals}></DataTableCell>
                                        <DataTableCell {...styles.dataCellsTotals}></DataTableCell>
                                        <DataTableCell {...styles.dataCellsTotals}>
                                            <LocalizedCurrency amount={currentYearTotalsToDate.allocated} />
                                        </DataTableCell>
                                        <DataTableCell {...styles.dataCellsTotals}>
                                            <LocalizedCurrency amount={currentYearTotalsToDate.spent} />
                                        </DataTableCell>
                                        <DataTableCell {...styles.dataCellsTotals}>
                                            <LocalizedCurrency amount={currentYearTotalsToDate.balance} />
                                        </DataTableCell>
                                    </DataTableRow>
                                )}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                )}
            </DataViewLoadingOverlay>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountAllowances),
    definition: {
        group: "My Account",
        allowedContexts: ["MyAccountPage"],
        displayName: "Budget Allowances",
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: basicTab,
                defaultValue: "",
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
